// @flow

import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter, Match, History } from 'react-router-dom';
import { ApolloConsumer } from 'react-apollo';
import { get } from 'lodash';
import { components, form, copy } from '@peachjar/components';
import { css } from 'react-emotion';
import * as Yup from 'yup';
import { Formik } from 'formik';
import parseAddress from 'parse-address';

import SchoolRegistrationForms from './SchoolRegistrationForms';
import WhiteSpaceSvgWrapper from '../../../../../components/WhiteSpaceSvgWrapper';
import DeskWoman from '../../../../../components/DeskWoman.svg';
import { loadRegistrationId } from '../../../onboarding.duck';
import TempRightActionHelper from '../../../components/TempRightActionHelper';
import {
  MAX_NUMBER_OF_SCHOOL_VARIANCE_ALLOWED,
  DISTRICT_NCES_ID_LENGTH,
  SCHOOL_NCES_ID_LENGTH,
  PRIVATE_SCHOOL_NCES_ID_LENGTH,
} from '../../../constants';

import CHECK_SCHOOL_QUERY from '../gql/ncesSchool.query';
import CHECK_DISTRICT_QUERY from '../gql/ncesDistrict.query';
import CHECK_ELIGIBILITY_QUERY from '../gql/schoolOrDistrictRegistrationEligibility.query';

const { quotes } = copy;
const { validators } = form;

const {
  FullSpread,
  ContentColumn,
  WhitespaceColumn,
  Notifications,
} = components;

const { notifyError } = Notifications;
const { isPhoneNumberValid } = validators;

const districtFormFields = [
  {
    id: 'districtName',
    label: 'District Name',
  },
  {
    id: 'physicalAddress',
    label: 'Physical Address',
  },
];
const schoolFormFields = [
  {
    id: 'districtName',
    label: 'District Name',
  },
  {
    id: 'schoolName',
    label: 'School Name',
  },
  {
    id: 'physicalAddress',
    label: 'Physical Address',
  },
];

const validationSchema = Yup.object().shape({
  contactName: Yup.string()
    .min(3)
    .max(256)
    .required(),
  contactPhoneNumber: Yup.string()
    .max(20)
    .required(),
  contactEmailAddress: Yup.string()
    .email()
    .required(),
  ncesId: Yup.string()
    .min(7)
    .max(12)
    .required(),
  schoolName: Yup.string()
    .min(3)
    .max(256),
  districtName: Yup.string()
    .min(3)
    .max(256)
    .required(),
  physicalAddress: Yup.string()
    .min(3)
    .max(256)
    .required(),
});

type Eligibility = {
  isEligible?: boolean,
  isEligibleAsIndependentSchool?: boolean,
  ineligibilityReason?: string,
};

type Props = {|
  +formValues: {
    numberOfSchools: string,
    discountCode: string,
  },
  +loadRegistrationId: (registrationId: string) => void,
  +onNotifyError: (message: string) => void,
  +nextPage: () => void,
  +previousPage: () => void,
  +match: Match,
  +history: History,
|};

type State = {|
  numSchoolsAssociatedToNcesId: number,
  orgNotFound: boolean,
  hasOrIsDistrictWithMultipleSchools: boolean,
  isCharterSchool: boolean,
  isCharterSchoolProceeding: boolean,
  eligibility: Eligibility,
|};

class SchoolRegistration extends Component<Props, State> {
  constructor() {
    super();

    this.state = {
      numSchoolsAssociatedToNcesId: 0,
      orgNotFound: false,
      hasOrIsDistrictWithMultipleSchools: false,
      eligibility: {
        isEligible: true,
        isEligibleAsIndependentSchool: null,
        ineligibilityReason: '',
      },
      isCharterSchool: null,
      isCharterSchoolProceeding: null,
    };
  }

  static parseDistrictId = ncesId => ncesId.substring(
    0,
    ncesId.length === 8 ?
      PRIVATE_SCHOOL_NCES_ID_LENGTH :
      DISTRICT_NCES_ID_LENGTH
  );

  handleCharterSchoolRadioSelection = value => () => {
    this.setState({
      isCharterSchoolProceeding: value,
    });
  };

  validateFormFields = values => {
    const { numberOfSchools } = this.props.formValues;
    const numberOfSchoolsInt = Number(numberOfSchools);
    const errors = {};

    if (values.contactPhoneNumber) {
      if (!isPhoneNumberValid(values.contactPhoneNumber)) {
        errors.contactPhoneNumber =
          'Invalid format. Please enter your number like so: (XXX) XXX-XXXX';
      }
    }

    if (values.physicalAddress) {
      const parsedAddress = parseAddress.parseLocation(values.physicalAddress);
      if (
        !parsedAddress.street ||
        !parsedAddress.city ||
        !parsedAddress.state ||
        !parsedAddress.zip
      ) {
        errors.physicalAddress =
          'Invalid format. Please enter a valid address.';
      }
    }

    if (values.ncesId) {
      if (numberOfSchoolsInt > 1) {
        // district
        if (values.ncesId.length !== DISTRICT_NCES_ID_LENGTH) {
          errors.ncesId = `Please enter a ${DISTRICT_NCES_ID_LENGTH}-digit district NCES ID.`;
        }
      } else if (numberOfSchoolsInt === 1) {
        // school
        if (
          values.ncesId.length !== SCHOOL_NCES_ID_LENGTH &&
          values.ncesId.length !== PRIVATE_SCHOOL_NCES_ID_LENGTH
        ) {
          errors.ncesId = `Please enter a ${SCHOOL_NCES_ID_LENGTH}-digit or ${PRIVATE_SCHOOL_NCES_ID_LENGTH}-digit school NCES ID.`;
        }
      }
    }

    if (values.contactEmailAddress) {
      if (values.contactEmailAddress === '') {
        errors.contactEmailAddress = 'Please provide contact email address.';
      }
    }

    return errors;
  };

  autofillNcesFields = async (
    values,
    numberOfSchoolsInt,
    setFieldValue,
    setFieldTouched,
    eligibility,
    isCharterSchool
  ) => {
    const { name, address, district, schools } = values;

    if (district === undefined) {
      // we have a district (they selected more than 1 school)

      setFieldValue('physicalAddress', address);
      setFieldValue('districtName', name);
      setFieldTouched('physicalAddress', true);
      setFieldTouched('districtName', true);

      this.setState({
        hasOrIsDistrictWithMultipleSchools: schools.length > 1,
        numSchoolsAssociatedToNcesId: schools.length,
        orgNotFound: false,
        eligibility,
        isCharterSchool: null,
      });
    } else {
      // we have a school (they selected only 1 school)

      setFieldValue('physicalAddress', address);
      setFieldValue('districtName', district.name);
      setFieldValue('schoolName', name);
      setFieldTouched('physicalAddress', true);
      setFieldTouched('districtName', true);
      setFieldTouched('schoolName', true);

      this.setState({
        hasOrIsDistrictWithMultipleSchools: district.schools.length > 1,
        numSchoolsAssociatedToNcesId: district.schools.length,
        orgNotFound: false,
        eligibility,
        isCharterSchool,
      });
    }
  };

  checkEligibility = async (client, ncesId): Eligibility => {
    // will take both school and district id
    const variables = { ncesId };
    const query = CHECK_ELIGIBILITY_QUERY;
    const { data } = await client.query({
      query,
      variables,
    });

    if (
      data &&
      Object.prototype.hasOwnProperty.call(
        data,
        'schoolOrDistrictRegistrationEligibility'
      )
    ) {
      return data.schoolOrDistrictRegistrationEligibility;
    }
  };

  checkNces = async (
    client,
    ncesId,
    numberOfSchoolsInt,
    setFieldValue,
    setFieldTouched
  ) => {
    if (
      ncesId.length !== DISTRICT_NCES_ID_LENGTH &&
      ncesId.length !== SCHOOL_NCES_ID_LENGTH &&
      ncesId.length !== PRIVATE_SCHOOL_NCES_ID_LENGTH
    ) {
      return;
    }

    const districtId = SchoolRegistration.parseDistrictId(ncesId);
    const id = ncesId.length > districtId.length ? ncesId : districtId;
    const variables = { id };
    const query =
      id.length > DISTRICT_NCES_ID_LENGTH
        ? CHECK_SCHOOL_QUERY
        : CHECK_DISTRICT_QUERY;

    const { data } = await client.query({
      query,
      variables,
    });

    if (data && (data.ncesDistrict || data.ncesSchool)) {
      // if found in our dataset but not a current customer
      const eligibility = await this.checkEligibility(client, ncesId);
      if (
        eligibility.isEligible === true ||
        eligibility.isEligibleAsIndependentSchool
      ) {
        if (data.ncesDistrict) {
          this.autofillNcesFields(
            data.ncesDistrict,
            numberOfSchoolsInt,
            setFieldValue,
            setFieldTouched,
            eligibility
          );
        } else if (data.ncesSchool) {
          this.autofillNcesFields(
            data.ncesSchool,
            numberOfSchoolsInt,
            setFieldValue,
            setFieldTouched,
            eligibility,
            get(data, 'ncesSchool.isCharter')
          );
        }
      } else {
        // ineligible
        this.setState({
          eligibility,
        });
      }
    } else {
      // not found in DB
      this.setState({
        hasOrIsDistrictWithMultipleSchools: false,
        numSchoolsAssociatedToNcesId: 0,
        isCharterSchool: null,
        orgNotFound: true,
        eligibility: {},
      });
    }
  };

  sendFormDataToDatabase = async (mutateFn, navigationFn, values) => {
    const { numberOfSchools, discountCode } = this.props.formValues;

    const {
      contactName,
      contactPhoneNumber,
      ncesId,
      schoolName,
      districtName,
      asIndependentSchool,
      physicalAddress: address,
      contactEmailAddress: email,
    } = values;

    const ncesDistrictId = SchoolRegistration.parseDistrictId(ncesId);

    const registrationInfo = {
      numberOfSchools,
      discountCode,
      contactName,
      contactPhoneNumber,
      ncesDistrictId,
      districtName,
      address,
      asIndependentSchool,
    };

    // set query with SchoolId if ncesId is for a school
    if ([SCHOOL_NCES_ID_LENGTH, PRIVATE_SCHOOL_NCES_ID_LENGTH].includes(ncesId.length)) {
      registrationInfo.ncesSchoolId = ncesId;
    }

    if (schoolName) {
      registrationInfo.schoolName = schoolName;
    }

    if (email) {
      registrationInfo.email = email;
    }

    const variables = {
      registrationInfo,
    };

    try {
      const registrationResponse = await mutateFn({ variables });
      const registrationId = registrationResponse.data.createRegistration.id;
      this.props.loadRegistrationId(registrationId);

      navigationFn();
    } catch (err) {
      const graphqlMessage = get(err, 'graphQLErrors[0].message');

      if (graphqlMessage) {
        this.props.onNotifyError(graphqlMessage);
      } else {
        this.props.onNotifyError('Something went wrong! Please try again.');
      }
    }
  };

  render() {
    const { nextPage, previousPage, formValues, match, history } = this.props;
    const { numberOfSchools } = formValues;

    const {
      orgNotFound,
      hasOrIsDistrictWithMultipleSchools,
      numSchoolsAssociatedToNcesId,
      eligibility,
      isCharterSchool,
      isCharterSchoolProceeding,
    } = this.state;

    const numberOfSchoolsInt = Number(numberOfSchools);

    const organization = numberOfSchoolsInt > 1 ? 'district' : 'school';
    const additionalFields =
      numberOfSchoolsInt > 1 ? districtFormFields : schoolFormFields;

    return (
      <div>
        <FullSpread>
          <ContentColumn
            onClick={previousPage}
            rightAction={<TempRightActionHelper />}
          >
            <div className={`p-3 ${css({ width: '70%' })}`}>
              <h1
                style={{ textAlign: 'left' }}
              >{`Great! Tell us a little bit about your ${organization}.`}</h1>
              {/*  FIXME no inline styles like this sadeagle */}
              <ApolloConsumer>
                {client => (
                  <Formik
                    initialValues={{
                      contactName: '',
                      contactPhoneNumber: '',
                      ncesId: '',
                      districtName: '',
                      schoolName: '',
                      physicalAddress: '',
                      contactEmailAddress: '',
                    }}
                    validationSchema={validationSchema}
                    validate={this.validateFormFields}
                    render={({
                      errors,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      values,
                    }) => {
                      const { ncesId } = values;

                      const didEnterSchoolIdLength =
                        (!!ncesId && ncesId.length === SCHOOL_NCES_ID_LENGTH) ||
                        ncesId.length === PRIVATE_SCHOOL_NCES_ID_LENGTH;

                      const isSoloSchoolInLargerDistrict =
                        !!ncesId &&
                        numberOfSchoolsInt === 1 &&
                        didEnterSchoolIdLength &&
                        hasOrIsDistrictWithMultipleSchools === true;

                      const didEnterSchoolIdInsteadOfDistrictId =
                        !!ncesId &&
                        didEnterSchoolIdLength &&
                        numberOfSchoolsInt > 1;

                      const didEnterDistrictIdInsteadOfSchoolId =
                        !!ncesId &&
                        ncesId.length === DISTRICT_NCES_ID_LENGTH &&
                        numberOfSchoolsInt === 1;

                      const didEnterCorrectNcesIdLength =
                        !!ncesId &&
                        ((numberOfSchoolsInt > 1 &&
                          ncesId.length === DISTRICT_NCES_ID_LENGTH) ||
                          (numberOfSchoolsInt === 1 && didEnterSchoolIdLength));

                      // if requested number of schools is not between acceptable range
                      const didExceedAllowedSchoolVariance =
                        numSchoolsAssociatedToNcesId !== 0 &&
                        !(
                          numberOfSchoolsInt <=
                            numSchoolsAssociatedToNcesId +
                              MAX_NUMBER_OF_SCHOOL_VARIANCE_ALLOWED &&
                          numberOfSchoolsInt >=
                            numSchoolsAssociatedToNcesId -
                              MAX_NUMBER_OF_SCHOOL_VARIANCE_ALLOWED
                        );

                      return (
                        <SchoolRegistrationForms
                          additionalFields={additionalFields}
                          checkNces={this.checkNces}
                          client={client}
                          didEnterCorrectNcesIdLength={
                            didEnterCorrectNcesIdLength
                          }
                          didEnterDistrictIdInsteadOfSchoolId={
                            didEnterDistrictIdInsteadOfSchoolId
                          }
                          didEnterSchoolIdInsteadOfDistrictId={
                            didEnterSchoolIdInsteadOfDistrictId
                          }
                          didExceedAllowedSchoolVariance={
                            didExceedAllowedSchoolVariance
                          }
                          eligibility={eligibility}
                          errors={errors}
                          handleChange={handleChange}
                          handleCharterSchoolRadioSelection={
                            this.handleCharterSchoolRadioSelection
                          }
                          handleBlur={handleBlur}
                          handleSubmit={handleSubmit}
                          history={history}
                          isCharterSchool={isCharterSchool}
                          isCharterSchoolProceeding={isCharterSchoolProceeding}
                          isSoloSchoolInLargerDistrict={
                            isSoloSchoolInLargerDistrict
                          }
                          isSubmitting={isSubmitting}
                          match={match}
                          nextPage={nextPage}
                          numberOfSchoolsInt={numberOfSchoolsInt}
                          numSchoolsAssociatedToNcesId={
                            numSchoolsAssociatedToNcesId
                          }
                          orgNotFound={orgNotFound}
                          previousPage={previousPage}
                          sendFormDataToDatabase={this.sendFormDataToDatabase}
                          setFieldTouched={setFieldTouched}
                          setFieldValue={setFieldValue}
                          touched={touched}
                          values={values}
                        />
                      );
                    }}
                  />
                )}
              </ApolloConsumer>
            </div>
          </ContentColumn>
          <WhitespaceColumn>
            <WhiteSpaceSvgWrapper
              svg={<DeskWoman />}
              quote={quotes.maldonado_helpsMeStayConnected}
            />
          </WhitespaceColumn>
        </FullSpread>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const form = state.form.onboarding;
  const formValues = form && form.values;
  const onboarding = state.onboarding;

  return {
    // "formValues" will be overridden by all typed-in values, ensuring new user input is captured
    // "initialValues" will be overridden by populated nces data from the fetch and will override typed-in values.
    // it's important that both of these exist for this page.
    // formValues: { ...onboarding, ...formValues },
    /**
     *  initialValues is a specific keyword needed by reduxForm to reinitialize
     *  a from with new values (for example, a server response like we are doing
     *  here) link: https://stackoverflow.com/questions/34271910/how-to-set-initialvalues-based-on-async-source-such-as-an-ajax-call-with-redux-f
     */
    formValues: { ...formValues, ...onboarding },
  };
};

const mapDispatchToProps = dispatch => ({
  loadRegistrationId: registrationId =>
    dispatch(loadRegistrationId(registrationId)),
  onNotifyError: message => dispatch(notifyError(message)),
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(SchoolRegistration)
);



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/SchoolRegistration/containers/SchoolRegistration.jsx